import { Controller, useFormContext } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import EditorInput, { EditorInputProps } from "../EditorInput";
import { VULNERABILITY_CATEGORIES } from "../../../../utils/api/endpoints/reports/reports";
import {
  FaFolder,
  FaFolderOpen,
  FaCodeBranch,
  FaSearch,
  FaChevronLeft,
  FaCheck
} from "react-icons/fa";

interface CategoryPath {
  categoryId: string;
  subcategoryId?: string;
  variantId?: string;
}

// Dropdown component with improved navigation
const HierarchicalCategoryDropdown = ({
  value,
  onChange
}: {
  value?: string;
  onChange: (value: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPath, setSelectedPath] = useState<CategoryPath | null>(null);
  const [navigationStack, setNavigationStack] = useState<string[]>([]);

  // Parse the initial value
  useEffect(() => {
    if (value) {
      try {
        const parsed = JSON.parse(value) as CategoryPath;
        setSelectedPath(parsed);
      } catch (e) {
        const category = VULNERABILITY_CATEGORIES.find(
          cat => cat.name === value
        );
        if (category) {
          setSelectedPath({ categoryId: category.id });
        } else {
          setSelectedPath(null);
        }
      }
    } else {
      setSelectedPath(null);
    }
  }, [value]);

  // Get display name for the selected path
  const getDisplayName = () => {
    if (!selectedPath) return "";

    const category = VULNERABILITY_CATEGORIES.find(
      c => c.id === selectedPath.categoryId
    );
    if (!category) return "";

    let result = category.name;

    if (selectedPath.subcategoryId && category.children) {
      const subcategory = category.children.find(
        sc => sc.id === selectedPath.subcategoryId
      );
      if (subcategory) {
        result += ` > ${subcategory.name}`;

        if (selectedPath.variantId && subcategory.children) {
          const variant = subcategory.children.find(
            v => v.id === selectedPath.variantId
          );
          if (variant) {
            result += ` > ${variant.name}`;
          }
        }
      }
    }

    return result;
  };

  // Navigate to a specific level
  const navigateTo = (level: string) => {
    const currentIndex = navigationStack.indexOf(level);
    if (currentIndex >= 0) {
      setNavigationStack(navigationStack.slice(0, currentIndex + 1));
    } else {
      setNavigationStack([...navigationStack, level]);
    }
  };

  // Go back one level
  const goBack = () => {
    if (navigationStack.length > 0) {
      setNavigationStack(navigationStack.slice(0, -1));
    }
  };

  // Get current items to display based on navigation stack
  const getCurrentItems = () => {
    if (navigationStack.length === 0) {
      return VULNERABILITY_CATEGORIES;
    }

    const categoryId = navigationStack[0];
    const category = VULNERABILITY_CATEGORIES.find(c => c.id === categoryId);

    if (!category || !category.children || navigationStack.length === 1) {
      return category?.children || [];
    }

    const subcategoryId = navigationStack[1];
    const subcategory = category.children.find(sc => sc.id === subcategoryId);

    if (!subcategory || !subcategory.children) {
      return [];
    }

    return subcategory.children;
  };

  // Search across all levels
  const searchResults = useMemo(() => {
    if (!searchQuery) return [];

    const results: Array<{
      id: string;
      name: string;
      path: CategoryPath;
      breadcrumb: string;
      type: "category" | "subcategory" | "variant";
    }> = [];

    const query = searchQuery.toLowerCase();

    // Search through categories
    VULNERABILITY_CATEGORIES.forEach(category => {
      if (category.name.toLowerCase().includes(query)) {
        results.push({
          id: category.id,
          name: category.name,
          path: { categoryId: category.id },
          breadcrumb: category.name,
          type: "category"
        });
      }

      // Search through subcategories
      if (category.children) {
        category.children.forEach(subcategory => {
          if (subcategory.name.toLowerCase().includes(query)) {
            results.push({
              id: subcategory.id,
              name: subcategory.name,
              path: { categoryId: category.id, subcategoryId: subcategory.id },
              breadcrumb: `${category.name} > ${subcategory.name}`,
              type: "subcategory"
            });
          }

          // Search through variants
          if (subcategory.children) {
            subcategory.children.forEach(variant => {
              if (variant.name.toLowerCase().includes(query)) {
                results.push({
                  id: variant.id,
                  name: variant.name,
                  path: {
                    categoryId: category.id,
                    subcategoryId: subcategory.id,
                    variantId: variant.id
                  },
                  breadcrumb: `${category.name} > ${subcategory.name} > ${variant.name}`,
                  type: "variant"
                });
              }
            });
          }
        });
      }
    });

    return results;
  }, [searchQuery]);

  // Handle selection
  const handleSelect = (path: CategoryPath) => {
    setSelectedPath(path);
    onChange(JSON.stringify(path));
    setOpen(false);
    setNavigationStack([]);
    setSearchQuery("");
  };

  // Handle item click based on current navigation
  const handleItemClick = (item: any) => {
    if (navigationStack.length === 0) {
      // Clicked on a category
      if (!item.children || item.children.length === 0) {
        // If no children, select this category
        handleSelect({ categoryId: item.id });
      } else {
        // Otherwise, navigate to subcategories
        navigateTo(item.id);
      }
    } else if (navigationStack.length === 1) {
      // Clicked on a subcategory
      const categoryId = navigationStack[0];
      if (!item.children || item.children.length === 0) {
        // If no children, select this subcategory
        handleSelect({ categoryId, subcategoryId: item.id });
      } else {
        // Otherwise, navigate to variants
        navigateTo(item.id);
      }
    } else if (navigationStack.length === 2) {
      // Clicked on a variant
      const [categoryId, subcategoryId] = navigationStack;
      handleSelect({
        categoryId,
        subcategoryId,
        variantId: item.id
      });
    }
  };

  // Check if two paths are equal
  const isPathEqual = (
    path1: CategoryPath | null,
    path2: CategoryPath | null
  ) => {
    if (!path1 || !path2) return false;
    return (
      path1.categoryId === path2.categoryId &&
      path1.subcategoryId === path2.subcategoryId &&
      path1.variantId === path2.variantId
    );
  };

  return (
    <div className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between rounded-md border bg-white px-3 py-2 text-left"
      >
        <span className="truncate">
          {selectedPath
            ? getDisplayName()
            : "🔍 Search for vulnerability category..."}
        </span>
        <span className="ml-2">▼</span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 max-h-80 w-full overflow-hidden rounded-md border bg-white shadow-lg">
          <div className="border-b p-2">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search vulnerability categories..."
                className="w-full rounded-md border px-3 py-2 pl-8"
              />
              <FaSearch className="absolute left-2.5 top-3 text-gray-400" />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {searchQuery ? (
              // Search results view
              <div>
                {searchResults.length === 0 ? (
                  <div className="p-3 text-center text-gray-500">
                    No results found
                  </div>
                ) : (
                  <ul>
                    {searchResults.map(result => (
                      <li
                        key={result.breadcrumb}
                        onClick={() => handleSelect(result.path)}
                        className="flex cursor-pointer items-center justify-between px-3 py-2 hover:bg-gray-100"
                      >
                        <div>
                          <div className="font-medium">{result.name}</div>
                          <div className="text-xs text-gray-500">
                            {result.breadcrumb}
                          </div>
                        </div>
                        {selectedPath &&
                          isPathEqual(selectedPath, result.path) && (
                            <FaCheck className="text-green-500" />
                          )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              // Navigation view
              <div>
                {navigationStack.length > 0 && (
                  <div className="flex items-center border-b p-2">
                    <button
                      onClick={goBack}
                      className="flex items-center text-sm font-medium text-blue-600"
                    >
                      <FaChevronLeft className="mr-1" />
                      Back
                    </button>
                    <div className="ml-2 text-sm font-medium">
                      {navigationStack
                        .map((id, index) => {
                          let name = "";
                          if (index === 0) {
                            const category = VULNERABILITY_CATEGORIES.find(
                              c => c.id === id
                            );
                            name = category?.name || "";
                          } else if (index === 1) {
                            const category = VULNERABILITY_CATEGORIES.find(
                              c => c.id === navigationStack[0]
                            );
                            const subcategory = category?.children?.find(
                              sc => sc.id === id
                            );
                            name = subcategory?.name || "";
                          }
                          return name;
                        })
                        .join(" > ")}
                    </div>
                  </div>
                )}

                <ul>
                  {getCurrentItems().map(item => {
                    const hasChildren =
                      "children" in item &&
                      item.children &&
                      item.children.length > 0;
                    let icon;

                    if (navigationStack.length === 0) {
                      icon = <FaFolder className="mr-2 text-yellow-500" />;
                    } else if (navigationStack.length === 1) {
                      icon = <FaFolderOpen className="mr-2 text-blue-500" />;
                    } else {
                      icon = <FaCodeBranch className="mr-2 text-green-600" />;
                    }

                    return (
                      <li
                        key={item.id}
                        onClick={() => handleItemClick(item)}
                        className="flex cursor-pointer items-center justify-between px-3 py-2 hover:bg-gray-100"
                      >
                        <div className="flex items-center">
                          {icon}
                          <span>{item.name}</span>
                        </div>
                        {hasChildren && <span>▶</span>}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const HierarchicalCategorySelector = ({
  name,
  inputTitle,
  details,
  required
}: EditorInputProps) => {
  const { control } = useFormContext();

  return (
    <EditorInput
      name={name}
      inputTitle={inputTitle}
      details={details}
      required={required}
    >
      <Controller
        name={name}
        control={control}
        rules={{ required }}
        render={({ field: { value, onChange } }) => (
          <HierarchicalCategoryDropdown value={value} onChange={onChange} />
        )}
      />
    </EditorInput>
  );
};

export default HierarchicalCategorySelector;
